import React, { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
	KnowledgeBaseArticle,
	KnowledgeBaseArticleContent,
	KnowledgeBaseArticleContentType,
	KnowledgeBaseArticleFlags,
} from 'types/KnowledgeBase';
import { Message } from 'primereact/message';
import { Spinner } from 'components/_new/Spinner';
import { Button } from 'components/_new/Button';
import { Link } from 'components/_new/Link';
import { MessageBox } from 'components/_new/MessageBox';
import { contentParser, ContentParserOptions } from 'utils/contentParser';
import { PdfPresenter } from 'components/_new/PdfPresenter/PdfPresenter';
import { toHtml } from 'components/_new/NewSlateEditor/utils/toHtml';
import { ArticlePresenterFeedback } from './ArticlePresenterFeedback';
import { ArticlePresenterFlags } from './ArticlePresenterFlags';

type ArticlePresenterContentProps = {
	readed?: boolean;
	article: KnowledgeBaseArticle;
	articleContent: KnowledgeBaseArticleContent;
	initialFlags?: KnowledgeBaseArticleFlags;
	options?: {
		disableRating?: boolean;
	};
	contentParserOptions?: ContentParserOptions;
	onFlagChange?: () => void;
};

export const ArticlePresenterContent = ({
	readed,
	article,
	articleContent,
	initialFlags,
	options,
	contentParserOptions,
	onFlagChange,
}: ArticlePresenterContentProps) => {
	const { t } = useTranslation();

	const { disableRating } = { disableRating: false, ...options };

	const prepareFilename = useCallback(() => {
		const extension = [KnowledgeBaseArticleContentType.FILE, KnowledgeBaseArticleContentType.PDF].includes(
			article.contentType
		)
			? `.pdf`
			: '';
		return `${articleContent.slug}${extension}`;
	}, [article.contentType, articleContent.slug]);

	const handleDownload = useCallback(() => {
		if (articleContent.signedUrl) {
			const downloadLink = document.createElement('a');
			downloadLink.href = articleContent.signedUrl;
			downloadLink.rel = 'noreferrer';
			downloadLink.target = '_blank';
			downloadLink.download = prepareFilename();
			document.body.append(downloadLink);
			downloadLink.click();
			setTimeout(() => {
				downloadLink.remove();
			}, 100);
		}
	}, [articleContent.signedUrl, prepareFilename]);

	const handleHtmlCopy = useCallback((event: ClipboardEvent) => {
		const selection = document.getSelection() as Selection;
		if (event) {
			event.preventDefault();
			(event as any).clipboardData.setData(
				'text',
				`${selection.toString()} - ${t('userPanel.files.contentCopyMessage')}`
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const parser = contentParser(contentParserOptions);

	const getParsedContent = useCallback(
		(content?: string | null) => {
			if (typeof content === 'string' && content.length > 0) {
				return <div onCopy={(event) => handleHtmlCopy(event as any)}>{parser.parse(String(content))}</div>;
			}
			return null;
		},
		[handleHtmlCopy, parser]
	);

	const renderFileContent = () => {
		switch (article.contentType) {
			case KnowledgeBaseArticleContentType.HTML_BLOCKS:
				return <div>UNSUPPORTED EDITOR CONTENT</div>;
			case KnowledgeBaseArticleContentType.HTML_WYSWIG:
				return <div className="user-content">{getParsedContent(articleContent.contentText)}</div>;
			case KnowledgeBaseArticleContentType.HTML_WYSWIG_2:
				return (
					<div
						className="user-content"
						dangerouslySetInnerHTML={{ __html: toHtml(articleContent.contentData) }}
					/>
				);
			case KnowledgeBaseArticleContentType.HTML:
				return getParsedContent(articleContent.content);
			case KnowledgeBaseArticleContentType.FILE:
				return (
					<div className="flex flex-column gap-2">
						<div className="user-content">{getParsedContent(articleContent.contentText)}</div>
						<div className="flex flex-row gap-2 justify-content-end">
							<Button
								label={t('misc.downloadFile')}
								onClick={handleDownload}
								disabled={!articleContent.signedUrl}
							/>
						</div>
					</div>
				);
			case KnowledgeBaseArticleContentType.VIDEO:
				// eslint-disable-next-line no-case-declarations
				let src = null;
				try {
					if (articleContent.url) {
						src = `https://www.youtube.com/embed/${(articleContent.url as string).split('=')[1]}`;
					}
				} catch {
					return 'Wystąpił błąd podczas ładwania filmu';
				}
				return (
					<div className="flex flex-column gap-2">
						<div className="user-content">{getParsedContent(articleContent.contentText)}</div>
						{src ? (
							<iframe
								width="100%"
								height="500px"
								src={src}
								title={articleContent.title}
								allowFullScreen
								allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
								referrerPolicy="strict-origin-when-cross-origin"
							/>
						) : null}
					</div>
				);
			case KnowledgeBaseArticleContentType.PDF:
				if (!articleContent.originalFilename || !articleContent.signedUrl) {
					return (
						<div className="flex flex-column gap-2">
							<div className="user-content">{getParsedContent(articleContent.contentText)}</div>
							<div>Nie można załadować pliku</div>
						</div>
					);
				}
				if (articleContent.originalFilename && articleContent.signedUrl) {
					const downloadFilename = prepareFilename();
					return (
						<div className="flex flex-column gap-2">
							<div className="user-content">{getParsedContent(articleContent.contentText)}</div>
							<PdfPresenter
								srcSet={{
									primary: articleContent.signedUrl,
									secondary: articleContent.signedUrlAlternative,
								}}
								title={downloadFilename}
							/>
							{/* <iframe
								className="iframe-view iframe-pdf"
								src={articleContent.signedUrlAlternative}
								title={downloadFilename}
								width="100%"
								height="500px"
								aria-label="pdf file preview"
								allowFullScreen
							/> */}
							<div className="text-center">
								<Trans
									t={t}
									i18nKey="userPanel.userFiles.openPdfAlternative"
									components={[
										<Link to={articleContent.signedUrl} target="_blank" variant="standard" />,
									]}
								/>
							</div>
							<div className="flex flex-row gap-2 justify-content-end">
								<Button label={t('misc.downloadFile')} onClick={handleDownload} />
							</div>
						</div>
					);
				}
				return (
					<div className="flex flex-column gap-2">
						{getParsedContent(articleContent.contentText)}
						<Spinner />
					</div>
				);
			case KnowledgeBaseArticleContentType.LINK:
				try {
					if (articleContent.url === null) {
						throw new Error('invalid url');
					} else {
						const url = new URL(articleContent.url);
						window.location.href = url.href;
						return <Spinner center />;
					}
				} catch {
					return t('userPanel.userFiles.file.invalidUrl');
				}
			default:
				return (
					<Message
						severity="error"
						text={t('misc.genericError')}
						className="w-full justify-content-start pl-4"
					/>
				);
		}
	};

	const [feadbackBump, setFeadbackBump] = useState({ primary: 0, secondary: 0 });
	return (
		<>
			{!disableRating && (
				<div className="flex flex-row gap-3">
					<ArticlePresenterFlags
						articleContentId={articleContent.id}
						initialFlags={initialFlags}
						onFlagChange={onFlagChange}
						readed={readed}
					/>
					<div className="hidden xl:block">
						<ArticlePresenterFeedback
							articleContentId={articleContent.id}
							onSuccess={() => setFeadbackBump((prev) => ({ ...prev, primary: prev.primary + 1 }))}
							bump={feadbackBump.secondary}
						/>
					</div>
				</div>
			)}
			{article.menteeAccess && article.mentorAccess && (
				<Message
					severity="info"
					text={t('userPanel.files.accessBoth')}
					className="w-full justify-content-start pl-4 mb-4"
				/>
			)}
			{article.menteeAccess && !article.mentorAccess && (
				<Message
					severity="info"
					text={t('userPanel.files.accessMenteesOnly')}
					className="w-full justify-content-start pl-4 mb-4"
				/>
			)}
			{!article.menteeAccess && article.mentorAccess && (
				<MessageBox
					icon="info-circle"
					iconSet="pi"
					variant="purple"
					message={t('userPanel.files.accessMentorsOnly')}
				/>
			)}
			<div className="p-knowledge-base-article">{renderFileContent()}</div>
			{!disableRating && (
				<ArticlePresenterFeedback
					articleContentId={articleContent.id}
					onSuccess={() => setFeadbackBump((prev) => ({ ...prev, secondary: prev.secondary + 1 }))}
					bump={feadbackBump.primary}
					big
				/>
			)}
		</>
	);
};
